import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useRenderGroup } from "@/components/ShareDesignPost/hooks/useRenderGroup";
import { DesignPackageServices } from "@/services/bedrock/DesignPackageServices";
import { DesignPackageDetail, PreviewSet } from "@/services/bedrock/dtos/DesignPackage";
import { DesignRenderGroup, RenderGroupStatus } from "@/services/bedrock/dtos/DesignRenderGroup";
import { designKeys } from "@/services/bedrock/queryKeys";

export type PreviewImageState = "ready" | "updating" | "missing";
export type PreviewRenderState = "idle" | "rendering" | "failed";

// Shared by every consumer (the preview panel and each All themes card) so the same theme and
// template set is only ever rendered once.
const requestedRenders = new Set<string>();

/**
 * Lets a package's previews render again with the same templates, for changes the template ids
 * don't capture (a new default background).
 */
export function forgetPreviewRenders(packageId: number): void {
  Array.from(requestedRenders)
    .filter(key => key.startsWith(`${packageId}:`))
    .forEach(key => requestedRenders.delete(key));
}

/** Test-only reset of the render dedupe. */
export function resetPreviewRenderDedupe(): void {
  requestedRenders.clear();
}

/** A one-off sample render of one slide with one of its backgrounds; never marked as the theme's preview. */
interface TriedBackground {
  itemId: number;
  themeId: number | null;
  assetId: number;
  groupId: number | null;
}

export interface TriedSlide {
  /** The background being tried. */
  assetId: number;
  url: string | null;
  status: RenderGroupStatus;
  /** The rendered sample, once it exists. */
  imageUrl: string | null;
}

function sampleImageUrl(group: DesignRenderGroup | undefined, designTemplateId: number | undefined): string | null {
  if (!group) return null;
  const render =
    group.designRenders.find(r => r.designTemplateId === designTemplateId && r.uri) ??
    group.designRenders.find(r => r.uri);
  return render?.uri ?? group.slideUrls[0] ?? null;
}

function groupInFlight(set: PreviewSet | undefined): boolean {
  return set?.group?.status === "pending" || set?.group?.status === "processing";
}

export function usePreviews(packageId: number, themeId: number | null, opts?: { includeCandidates?: boolean }) {
  const includeCandidates = !!opts?.includeCandidates;
  const queryClient = useQueryClient();
  const queryKey = designKeys.previews(packageId, themeId, includeCandidates);

  const { data: set, isLoading } = useQuery({
    queryKey,
    queryFn: () => DesignPackageServices.previews(packageId, { themeIds: [themeId], includeCandidates }),
    staleTime: 60_000,
    select: (sets: PreviewSet[]) => sets[0],
  });

  const [groupId, setGroupId] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const [renderKey, setRenderKey] = useState<string | null>(null);
  const { group } = useRenderGroup(groupId);

  useEffect(() => {
    setGroupId(null);
    setFailed(false);
    setRenderKey(null);
  }, [packageId, themeId]);

  const groupStatus = group?.status;
  useEffect(() => {
    if (groupStatus === "success") {
      queryClient.invalidateQueries({ queryKey: designKeys.previews(packageId, themeId, includeCandidates) });
    } else if (groupStatus === "failed") {
      setFailed(true);
      if (renderKey) requestedRenders.delete(renderKey);
    }
  }, [groupStatus, renderKey, packageId, themeId, includeCandidates, queryClient]);

  const slideFor = useCallback(
    (itemId: number): { url: string | null; state: PreviewImageState } => {
      const slide = set?.slides.find(s => s.designPackageItemId === itemId);
      if (!slide) {
        // The master set is the detail's own preview, so there is something to show before it loads.
        const item =
          themeId === null
            ? queryClient
                .getQueryData<DesignPackageDetail>(designKeys.detail(packageId))
                ?.items.find(i => i.id === itemId)
            : undefined;
        const url = item?.activeTemplate.previewUrl ?? null;
        return url ? { url, state: "ready" } : { url: null, state: "missing" };
      }
      if (slide.status === "success") return { url: slide.url, state: "ready" };
      if (slide.url) return { url: slide.url, state: slide.status === "failed" ? "ready" : "updating" };
      return { url: null, state: "missing" };
    },
    [set, themeId, queryClient, packageId]
  );

  const activeTemplateIds = useCallback((): number[] => {
    const detail = queryClient.getQueryData<DesignPackageDetail>(designKeys.detail(packageId));
    if (detail) return [...detail.items].sort((a, b) => a.position - b.position).map(i => i.activeTemplate.id);
    return (set?.slides ?? []).map(s => s.designTemplateId);
  }, [queryClient, packageId, set]);

  const needsRender = useCallback((): boolean => {
    if (!set || groupInFlight(set)) return false;
    const detail = queryClient.getQueryData<DesignPackageDetail>(designKeys.detail(packageId));
    const itemIds = detail ? detail.items.map(i => i.id) : set.slides.map(s => s.designPackageItemId);
    return itemIds.some(itemId => {
      const slide = set.slides.find(s => s.designPackageItemId === itemId);
      if (!slide) return true;
      return slide.status === "stale" || slide.status === "failed" || (slide.status === "pending" && !set.group);
    });
  }, [set, queryClient, packageId]);

  const render = useCallback(
    (key: string) => {
      requestedRenders.add(key);
      setRenderKey(key);
      setFailed(false);
      DesignPackageServices.renderPreview(packageId, { themeId })
        .then(created => {
          setGroupId(created.id);
          queryClient.invalidateQueries({ queryKey: designKeys.previews(packageId, themeId, includeCandidates) });
        })
        .catch(() => {
          requestedRenders.delete(key);
          setFailed(true);
        });
    },
    [packageId, themeId, includeCandidates, queryClient]
  );

  const ensureRendered = useCallback(() => {
    if (!needsRender()) return;
    const key = `${packageId}:${themeId}:${activeTemplateIds().join(",")}`;
    if (requestedRenders.has(key)) return;
    render(key);
  }, [needsRender, packageId, themeId, activeTemplateIds, render]);

  const retry = useCallback(() => {
    const key = `${packageId}:${themeId}:${activeTemplateIds().join(",")}`;
    requestedRenders.delete(key);
    render(key);
  }, [packageId, themeId, activeTemplateIds, render]);

  const { data: storedTried } = useQuery<TriedBackground | null>({
    queryKey: designKeys.triedBackground(packageId),
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
  });
  const tried = storedTried && storedTried.themeId === themeId ? storedTried : null;
  const { group: triedGroup } = useRenderGroup(tried?.groupId ?? null);
  // Only the instance that started a try reports its failure, so the toast shows once.
  const startedTries = useRef(new Set<number>());

  const readTried = useCallback(
    () => queryClient.getQueryData<TriedBackground | null>(designKeys.triedBackground(packageId)) ?? null,
    [queryClient, packageId]
  );
  const setTried = useCallback(
    (next: TriedBackground | null) =>
      queryClient.setQueryData<TriedBackground | null>(designKeys.triedBackground(packageId), next),
    [queryClient, packageId]
  );

  const keepCurrent = useCallback(() => setTried(null), [setTried]);

  const previousThemeId = useRef(themeId);
  useEffect(() => {
    const previous = previousThemeId.current;
    previousThemeId.current = themeId;
    if (previous === themeId) return;
    if (readTried()?.themeId === previous) setTried(null);
  }, [themeId, readTried, setTried]);

  const triedStatus = triedGroup?.status;
  useEffect(() => {
    if (triedStatus !== "failed" || !tried?.groupId || !startedTries.current.has(tried.groupId)) return;
    startedTries.current.delete(tried.groupId);
    toast.error("We couldn't render this preview.");
    setTried(null);
  }, [triedStatus, tried?.groupId, setTried]);

  const tryBackground = useCallback(
    (itemId: number, { assetId }: { assetId: number }) => {
      const detail = queryClient.getQueryData<DesignPackageDetail>(designKeys.detail(packageId));
      const designTemplateId = detail?.items.find(i => i.id === itemId)?.activeTemplate.id;
      const entry: TriedBackground = { itemId, themeId, assetId, groupId: null };
      setTried(entry);
      const stillCurrent = () => {
        const current = readTried();
        return current?.itemId === itemId && current.themeId === themeId && current.assetId === assetId;
      };
      DesignPackageServices.renderPreview(packageId, {
        themeId,
        ...(designTemplateId != null ? { designTemplateId } : {}),
        backgroundAssetId: assetId,
      })
        .then(created => {
          if (!stillCurrent()) return;
          startedTries.current.add(created.id);
          queryClient.setQueryData(designKeys.renderGroup(created.id), created);
          setTried({ ...entry, groupId: created.id });
        })
        .catch(() => {
          if (!stillCurrent()) return;
          toast.error("We couldn't render this preview.");
          setTried(null);
        });
    },
    [queryClient, packageId, themeId, readTried, setTried]
  );

  const triedSlide = useCallback(
    (itemId: number): TriedSlide | null => {
      if (!tried || tried.itemId !== itemId) return null;
      const detail = queryClient.getQueryData<DesignPackageDetail>(designKeys.detail(packageId));
      const template = detail?.items.find(i => i.id === itemId)?.activeTemplate;
      const url = template?.backgrounds.find(b => b.assetId === tried.assetId)?.url ?? null;
      if (tried.groupId == null || !triedGroup) {
        return { assetId: tried.assetId, url, status: "pending", imageUrl: null };
      }
      const designTemplateId = template?.id;
      return {
        assetId: tried.assetId,
        url,
        status: triedGroup.status,
        imageUrl: triedGroup.status === "success" ? sampleImageUrl(triedGroup, designTemplateId) : null,
      };
    },
    [tried, triedGroup, queryClient, packageId]
  );

  const ownGroupInFlight = groupStatus === "pending" || groupStatus === "processing";
  const renderState: PreviewRenderState = failed
    ? "failed"
    : ownGroupInFlight || groupInFlight(set) || (groupId == null && renderKey != null)
    ? "rendering"
    : "idle";

  return { set, isLoading, slideFor, ensureRendered, renderState, retry, tryBackground, triedSlide, keepCurrent };
}
